import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit, Output,
  signal,
  SimpleChanges
} from '@angular/core';
import {
  FormControl,
  FormControlOptions,
  FormGroup,
  Validators
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Attachment, FilesEvent } from 'src/app/model/file.model';
import { WeatherService } from 'src/app/services/weather.service';
import { finalize, pairwise, startWith, Subscription, tap } from 'rxjs';
import { AttachmentService } from 'src/app/services/attachment.service';
import { HttpEventType } from '@angular/common/http';
import { StoreService } from 'src/app/services/store.service';
import { Task } from 'src/app/modules/go/model/task.model';
import {
  Control, FailureCodeModel, OneArgValidatorDef,
  OneArgValidators,
  ValidationErrorMessages
} from '../../../go/model/form.model';
import {
  getErrorMessage,
  getValidatorFn, isAttachmentArray,
  isAttachmentUploadEvent
} from '../../../go/utils/form.utils';
import { getTaskGps } from '../../../go/utils/task.utils';
import { BaseControlModel } from '../../../../model/general.model';
import { min2Files } from './custom.validator';
import { DynamicFormService } from "../../../../services/dynamic-form.service";
import { ConnectionStatusService } from 'src/app/services/connection-status.service';

@Component({
  selector: 'dynamic-control',
  templateUrl: './dynamic-control.component.html',
  styleUrls: ['./dynamic-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicControlComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup;
  @Input() control: Control;
  @Input() controlName: string;
  @Input() label?: string;
  @Input() customLabel?: string;
  @Input() customValue?: string;
  @Input() options?: FormControlOptions;
  @Input() value: any;
  @Input() isFailure: boolean | null;
  @Input() description?: string;
  @Input() readonly = false;
  @Input() task: Task;
  @Input() previewMode = false;
  @Input() failureCodes?: FailureCodeModel[];
  @Input() index: number;
  @Input() validationRefreshTick = 0;
  @Output() valueChanged = new EventEmitter<BaseControlModel>
  @Output() failureHistoryEvent = new EventEmitter<void>;

  showControl = signal(true);
  formControl: FormControl;
  today = new Date();
  mapReady = signal(false);
  valueChecker$: Subscription;
  private sub = new Subscription();
  protected networkStatus$ = this.connectionStatusService.networkStatus$;

  constructor(
    private translate: TranslateService,
    private attachmentService: AttachmentService,
    private weatherService: WeatherService,
    private storeService: StoreService,
    private dynamicFormService: DynamicFormService,
    private cdr: ChangeDetectorRef,
    private connectionStatusService: ConnectionStatusService
  ){}
  ngOnInit(): void {
    this.formControl = new FormControl(this.getControlValue(), this.getOptions(this.control))

    if (!this.control.enableIfFailure && this.control.required) {
      this.formControl.setValidators(Validators.required)
    }
    this.form?.addControl(this.controlName, this.formControl);
    this.applyReadonlyState();

    this.handleMapControl();
    this.handlePhotoControl()

    this.valueChecker$ = this.formControl.valueChanges.pipe(startWith(this.formControl.value), pairwise()).subscribe(([previousValue, value]) => {
      this.valueChanged.emit({ id: this.controlName, value, previousValue })
    });

    this.sub.add(
      this.dynamicFormService.onFillInForm().subscribe(() => this.setDefaultValue())
    )
  }

  ngOnDestroy() {
    if (this.valueChecker$) {
      this.valueChecker$.unsubscribe();
    }
    this.sub.unsubscribe();
  }

  handleMapControl(): void {
    if (this.control.type === 'MAP') {
      this.mapReady.set(false);
      setTimeout(() => {
        this.mapReady.set(true);
      }, 0);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['validationRefreshTick'] && !changes['validationRefreshTick'].firstChange) {
      this.cdr.markForCheck();
    }

    if (changes['readonly'] && this.formControl) {
      this.applyReadonlyState();
    }

    if (changes['isFailure']) {
      const isFailure = changes['isFailure'].currentValue;
      this.showControl.set(!this.control.enableIfFailure || isFailure);

      if (this.control.enableIfFailure && this.formControl) {
        this.formControl.setValidators((this.showControl() && this.control.required) ? [Validators.required] : null);
        this.formControl.updateValueAndValidity();
      }
    }
  }

  private getControlValue() {

    if (this.control.type === 'DATE' && !this.value) {
      return this.today;
    }
    if (this.value != null) {
      return this.value
    }


  }

  getOptions(control: Control) {
    const options = {} as FormControlOptions;

    if(control.validators){
      options.validators = control.validators.map(getValidatorFn);
    }
    return options;
  }

  getErrorMessages(): string[] {
    const control = this.formControl;
    if (!control || !control.errors) {
      return [];
    }

    return Object.keys(control.errors).map(e => this.translate.instant(getErrorMessage(e as ValidationErrorMessages), control.errors?.[e] ?? {}));
  }

  private findArgumentValueForValidator(validatorType: OneArgValidators) {
    const validator = this.control.validators?.find(v => v.type === validatorType);
    if(validator) {
      return (validator as OneArgValidatorDef).argument;
    }
    return null;
  }
  getMaxLength() {
    return (this.findArgumentValueForValidator('MAX_LENGTH'));
  }
  getMinLength() {
    return (this.findArgumentValueForValidator('MIN_LENGTH'));
  }
  getMinValue() {
    return this.findArgumentValueForValidator('MIN');
  }
  getMaxValue() {
    return this.findArgumentValueForValidator('MAX');
  }

  hasHistoricalFailures(): boolean {
    return this.index === 0
      && !!this.failureCodes?.length
      && !this.previewMode
      && (this.failureCodes || []).some(el => (this.task.historicalFailureCodes || []).includes(el.code));
  }

  uploadFile(event: FilesEvent){
    const updateControlValue = (body: Attachment[]) => {
      let value = this.formControl.value;
      value = [...value, ...body];
      this.formControl.setValue(value);
    }

    void this.storeService.showLoader()
    this.attachmentService.upload(
      event.data,
      event.type,
      this.task.id,
      getTaskGps(this.task),
      stage => this.storeService.setLoaderText(stage)
    )
    .pipe(
      tap((event) => {
        if(isAttachmentUploadEvent(event)) {
          event.type === HttpEventType.UploadProgress && this.storeService.setUploadProgress(event)
        }
      }),
      tap(event => {
        if(isAttachmentArray(event)) {
          updateControlValue(event);
        }
        else {
          if(event.type === HttpEventType.Response){
            updateControlValue(event.body ?? []);
          }
        }

      }),
      finalize(() => {
        this.storeService.hideLoader();
        this.storeService.loaderText = '';
      })
    )
    .subscribe();
  }

  deleteFile(event: any) {
    let value = this.formControl.value;
    if(value) {
      this.storeService.showLoader();
      this.attachmentService.delete(value.id)
      .pipe(
        tap(() => this.formControl.setValue(value.filter((v: Attachment) => v.id !== event.id))),
        finalize(() => this.storeService.hideLoader())
      )
      .subscribe();
    }
  }

  refreshTemperature(){
    this.formControl.disable();
    this.weatherService.getCurrentWeather()
    .pipe(
      tap(weather => this.formControl.setValue(weather.current.temp_c)),
      finalize(() => this.formControl.enable())
    )
    .subscribe();
  }

  setLocation(event: number[]) {
    this.formControl.setValue(event, { emitEvent: false });
  }

  getFileError() {
    let msg = '1_PHOTO_REQUIRED';
    if (this.task.templateImplementationVersion === 2) {
      msg = '2_PHOTO_REQUIRED';
    }
    return this.translate.instant(msg);
  }

  get photoHintKey(): string {
    switch (this.task.templateImplementationVersion) {
      case 1: return 'UPLOAD_LIMIT';
      case 2: return 'UPLOAD_2_10_FILES';
      case 3: return 'UPLOAD_1_4_FILES';
      default: return 'UPLOAD_LIMIT';
    }
  }

  get photoMaxFiles(): number {
    return this.task.templateImplementationVersion === 3 ? 4 : 10;
  }

  private handlePhotoControl() {
    if (this.control.type === 'PHOTO') {
      if (this.control.required) {
        this.formControl.setValidators(Validators.required);
        if (this.task.templateImplementationVersion === 2) {
          this.formControl.setValidators(min2Files())
        }
      }
    }
  }

  private setDefaultValue(): void {
    if (this.formControl.value || this.hasHistoricalFailures()) {
      return;
    }

    const options = this.control.options;
    if (!options?.some(option => option.defaultValue)) {
      return;
    }

    switch (this.control.type) {
      case 'LIST':
      case 'RADIO': {
        const defaultOption = options.find(option => option.defaultValue);
        if (defaultOption) {
          this.formControl.setValue(defaultOption.value);
        }
        break;
      }
      case 'MULTISELECT': {
        const defaultValues = options
          .filter(option => option.defaultValue)
          .map(option => option.value);
        this.formControl.setValue(defaultValues);
        break;
      }
    }
  }

  private applyReadonlyState(): void {
    if (this.readonly) {
      this.formControl.disable({ emitEvent: false });
    } else {
      this.formControl.enable({ emitEvent: false });
    }

    this.cdr.markForCheck();
  }

}
